// ─────────────────────────────────────────────────────────────────────────────
// UNIFIED GARMENT LOADER
// Loads a GarmentSchema and resolves all assets (GLB + texture + image).
// Returns a LoadedGarment with everything the render pipeline needs.
// ─────────────────────────────────────────────────────────────────────────────

import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import type { GarmentSchema, LoadedGarment } from "./garment-schema";

const _cache = new Map<string, LoadedGarment>();
const _gltfLoader = new GLTFLoader();
const _texLoader  = new THREE.TextureLoader();

// ── Catalog fetch + cache ─────────────────────────────────────────────────────
let _catalog: GarmentSchema[] | null = null;

export async function loadCatalog(url = "/garments/catalog.json"): Promise<GarmentSchema[]> {
  if (_catalog) return _catalog;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`[GarmentLoader] Failed to fetch catalog: ${res.status}`);
  _catalog = await res.json() as GarmentSchema[];
  return _catalog;
}

export function getCatalog(): GarmentSchema[] {
  return _catalog ?? [];
}

export function getGarmentById(id: string): GarmentSchema | undefined {
  return _catalog?.find(g => g.id === id);
}

// ── Load a single garment — 3D primary, PNG fallback ─────────────────────────
// Always attempts GLB + texture. Also loads the PNG image in parallel
// so the 2D fallback is instantly ready if GLB is missing or fails.
export async function loadGarment(
  schema: GarmentSchema,
  mode: "2d" | "3d" | "auto" = "auto"
): Promise<LoadedGarment> {
  const key = `${schema.id}:${mode}`;
  if (_cache.has(key)) return _cache.get(key)!;

  // 3D is always attempted when GLB path is defined.
  // PNG image is always loaded in parallel as 2D fallback.
  const [image, glbScene, texture] = await Promise.all([
    // Always load the PNG — used as 2D fallback and for thumbnail
    schema.assets.image
      ? _loadImage(schema.assets.image).catch(() => null)
      : Promise.resolve(null),

    // Always attempt GLB — null if missing/404
    schema.assets.glb && mode !== "2d"
      ? _loadGLB(schema.assets.glb).catch(err => {
          console.warn(`[GarmentLoader] GLB failed for "${schema.id}" — 2D fallback active`, err);
          return null;
        })
      : Promise.resolve(null),

    // Texture for 3D — null if missing
    schema.assets.texture && mode !== "2d"
      ? _loadTexture(schema.assets.texture).catch(() => null)
      : Promise.resolve(null),
  ]);

  const loaded: LoadedGarment = { schema, image, glbScene, texture };
  _cache.set(key, loaded);

  console.log(
    `[GarmentLoader] "${schema.id}" loaded — 3D:${!!glbScene} 2D:${!!image}`
  );

  return loaded;
}

// ── Preload entire catalog in background ─────────────────────────────────────
export async function preloadCatalog(
  mode: "2d" | "3d" | "auto" = "3d",  // 3D primary — always preload GLB + PNG
  onProgress?: (loaded: number, total: number) => void
) {
  const catalog = await loadCatalog();
  let loaded = 0;
  await Promise.allSettled(
    catalog.map(async schema => {
      await loadGarment(schema, mode).catch(err =>
        console.warn(`[GarmentLoader] Failed to preload ${schema.id}:`, err)
      );
      loaded++;
      onProgress?.(loaded, catalog.length);
    })
  );
}

// ── Clear cache (useful on context loss) ─────────────────────────────────────
export function clearGarmentCache() {
  _cache.forEach(g => {
    g.glbScene?.traverse(obj => {
      if (obj instanceof THREE.Mesh) {
        obj.geometry?.dispose();
        if (Array.isArray(obj.material)) {
          obj.material.forEach(m => m.dispose());
        } else {
          obj.material?.dispose();
        }
      }
    });
    g.texture?.dispose();
  });
  _cache.clear();
}

// ── Internals ────────────────────────────────────────────────────────────────
function _loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload  = () => resolve(img);
    img.onerror = () => reject(new Error(`[GarmentLoader] Image failed: ${src}`));
    img.src = src;
  });
}

function _loadGLB(url: string): Promise<THREE.Group> {
  return new Promise((resolve, reject) => {
    _gltfLoader.load(
      url,
      gltf => {
        const scene = gltf.scene;
        scene.traverse(obj => {
          if (obj instanceof THREE.SkinnedMesh) {
            obj.frustumCulled = false;
            // Ensure skinning uniforms are correct
            if (obj.material) {
              // skinning is handled by SkinnedMesh internally in Three.js r152+
              void obj.material;
            }
          }
        });
        resolve(scene);
      },
      undefined,
      reject
    );
  });
}

function _loadTexture(url: string): Promise<THREE.Texture> {
  return new Promise((resolve, reject) => {
    _texLoader.crossOrigin = "anonymous";
    _texLoader.load(
      url,
      tex => {
        tex.colorSpace  = THREE.SRGBColorSpace;
        tex.generateMipmaps = true;
        tex.minFilter   = THREE.LinearMipmapLinearFilter;
        tex.magFilter   = THREE.LinearFilter;
        tex.needsUpdate = true;
        resolve(tex);
      },
      undefined,
      reject
    );
  });
}
